import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Settings, MapPin, Building2, Truck, Package,
  Smartphone, CreditCard, DollarSign, ShoppingCart, Wrench,
  Server, Cloud, Database, Network, Wifi, Scale, Shield, Users,
  Activity, Mail, ChevronDown, ChevronRight, X, AlertTriangle, Calendar,
  Printer, FileText, FileCheck, Bell, HardDrive, Phone, Monitor, Package2,
  Cable, BarChart3, TrendingUp, Megaphone, Search, Cpu, Laptop, Router,
  Camera, Video, Lock, Key, Folder, FolderOpen, Archive, Box, Boxes,
  ClipboardList, ClipboardCheck, ListTodo, CheckSquare, FileSpreadsheet,
  Receipt, Wallet, PiggyBank, CircleDollarSign, BadgeDollarSign, Banknote,
  Globe, Link, ExternalLink, Share2, MessageSquare, MessagesSquare, Send,
  UserPlus, UserMinus, UserCheck, UserCog, UsersRound, Contact, BookUser,
  Briefcase, Building, Home, Store, Warehouse, Factory, Landmark,
  HelpCircle, Info, AlertCircle, XCircle, CheckCircle, Clock, Timer,
  Zap, Power, PlugZap, Battery, BatteryCharging, Sun, Moon, Lightbulb,
  Headphones, Speaker, Mic, Radio, Tv, Gamepad2, MousePointer, Keyboard,
  ScanLine, QrCode, Barcode, Tag, Tags, Bookmark, Star, Heart, Flag,
  Target, Crosshair, Compass, Navigation, Map, Route, Milestone, Signpost,
  Gauge, Thermometer, Droplet, Wind, Flame, Snowflake, NotebookPen
} from 'lucide-react';
import { useMenuData } from '../hooks/useMenuData';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import { useVersionCheck } from '../hooks/useVersionCheck';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>(['arac-gerec']);
  const [systemTitle, setSystemTitle] = useState('IT Toolbox');
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const { user, hasRole } = useAuth();
  const { t, language } = useLanguage();
  const { hasUpdate, currentVersion, latestVersion } = useVersionCheck();

  // ✅ Get menus from database (Menu Management)
  const { menuItems: dynamicMenuItems, loading: menuLoading, error } = useMenuData();

  // Use dynamic menu items from database (Menu Management page)
  const menuItemsToRender = dynamicMenuItems;

  // Dashboard'a git
  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  // Sistem ayarlarını yükle
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const settings = await apiService.getSystemSettings();
        
        // System title'ı al - settings'in array olduğundan emin ol
        if (Array.isArray(settings)) {
          const titleSetting = settings.find((s: any) => s.setting_key === 'system_title');
          if (titleSetting) {
            setSystemTitle(titleSetting.setting_value);
          }
        }
        
        // System logo'yu kontrol et - önce settings'den, sonra dosyadan
        if (Array.isArray(settings)) {
          const logoSetting = settings.find((s: any) => s.setting_key === 'system_logo');
          if (logoSetting && logoSetting.setting_value) {
            const timestamp = new Date().getTime();
            // Backend'den gelen path'i kullan
            setSystemLogo(`${logoSetting.setting_value}?t=${timestamp}`);
          } else {
            // Settings'de yoksa dosyayı kontrol et
            try {
              const timestamp = new Date().getTime();
              const logoResponse = await fetch(`/uploads/logo.png?t=${timestamp}`);
              if (logoResponse.ok) {
                setSystemLogo(`/uploads/logo.png?t=${timestamp}`);
              } else {
                setSystemLogo(null);
              }
            } catch (error) {
              setSystemLogo(null);
            }
          }
        } else {
          // Settings yüklenemediyse direkt dosyayı kontrol et
          try {
            const timestamp = new Date().getTime();
            const logoResponse = await fetch(`/uploads/logo.png?t=${timestamp}`);
            if (logoResponse.ok) {
              setSystemLogo(`/uploads/logo.png?t=${timestamp}`);
            } else {
              setSystemLogo(null);
            }
          } catch (error) {
            setSystemLogo(null);
          }
        }
      } catch (error) {
        console.error('Error loading system settings:', error);
      }
    };

    loadSystemSettings();

    // Logo güncelleme event listener'ı
    const handleLogoUpdate = (event: CustomEvent) => {
      if (event.detail?.logoPath) {
        setSystemLogo(event.detail.logoPath);
      } else {
        // Eğer detail yoksa tüm ayarları yeniden yükle
        loadSystemSettings();
      }
    };

    // Favicon güncelleme event listener'ı
    const handleFaviconUpdate = (event: CustomEvent) => {
      if (event.detail?.faviconPath) {
        // Favicon güncelleme işlemi burada yapılabilir
      }
    };

    // Sistem ayarları güncelleme event listener'ı
    const handleSystemSettingsUpdate = () => {
      loadSystemSettings();
    };

    // Event listener'ları ekle
    window.addEventListener('logoUpdated', handleLogoUpdate as EventListener);
    window.addEventListener('faviconUpdated', handleFaviconUpdate as EventListener);
    window.addEventListener('systemSettingsUpdated', handleSystemSettingsUpdate);
    
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate as EventListener);
      window.removeEventListener('faviconUpdated', handleFaviconUpdate as EventListener);
      window.removeEventListener('systemSettingsUpdated', handleSystemSettingsUpdate);
    };
  }, []);

  const iconComponents: { [key: string]: React.ComponentType<any> } = {
    // Temel
    LayoutDashboard, Settings, MapPin, Building2, Truck, Package,
    Smartphone, CreditCard, DollarSign, ShoppingCart, Wrench,
    Server, Cloud, Database, Network, Wifi, Scale, Shield, Users,
    Activity, Mail, AlertTriangle, Calendar, Printer, FileText, FileCheck, Bell,
    // Donanım
    HardDrive, Phone, Monitor, Package2, Cable, Cpu, Laptop, Router,
    Camera, Video, Keyboard, MousePointer, Headphones, Speaker, Mic, Radio, Tv, Gamepad2,
    // Analiz & Raporlama
    BarChart3, TrendingUp, Gauge,
    // İletişim
    Megaphone, MessageSquare, MessagesSquare, Send,
    // Arama & Navigasyon
    Search, Globe, Link, ExternalLink, Share2, Navigation, Map, Route, Compass,
    // Kullanıcı
    UserPlus, UserMinus, UserCheck, UserCog, UsersRound, Contact, BookUser,
    // Yapı & Lokasyon
    Briefcase, Building, Home, Store, Warehouse, Factory, Landmark,
    // Dosya & Organizasyon
    Folder, FolderOpen, Archive, Box, Boxes, ClipboardList, ClipboardCheck, ListTodo, CheckSquare, FileSpreadsheet,
    // Finans
    Receipt, Wallet, PiggyBank, CircleDollarSign, BadgeDollarSign, Banknote,
    // Güvenlik
    Lock, Key,
    // Durum
    HelpCircle, Info, AlertCircle, XCircle, CheckCircle, Clock, Timer,
    // Enerji
    Zap, Power, PlugZap, Battery, BatteryCharging, Sun, Moon, Lightbulb,
    // Etiketleme
    ScanLine, QrCode, Barcode, Tag, Tags, Bookmark, Star, Heart, Flag, Target, Crosshair, Milestone, Signpost,
    // Çevre
    Thermometer, Droplet, Wind, Flame, Snowflake,
    // Not Defteri
    NotebookPen
  };

  const toggleExpanded = (itemId: string | number) => {
    const id = String(itemId);
    setExpandedItems(prev =>
      prev.includes(id)
        ? prev.filter(existingId => existingId !== id)
        : [...prev, id] // Keep parent menus expanded when opening child menus
    );
  };

  const handleNavigation = (href: string) => {
    const page = href.replace('/', '');
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
    onClose();
  };

  const renderMenuItem = (item: any, level = 0) => {
    if (!item) {
      return null;
    }
    
    // Dinamik menü için farklı property isimleri kullan
    const itemId = String(item.id || item.id || '');
    const itemLabelRaw = String(item.name || item.label || '');
    const itemLabel = (language === 'en' && item.name_en) ? item.name_en : itemLabelRaw;
    const itemHref = item.route || item.href;
    const itemIcon = item.icon;
    
    
    // Role-based permission kontrolü
    if (item.roles && Array.isArray(item.roles) && item.roles.length > 0) {
      if (!hasRole(item.roles)) {
        return null;
      }
    }

    const IconComponent = itemIcon && iconComponents[itemIcon as keyof typeof iconComponents] 
      ? iconComponents[itemIcon as keyof typeof iconComponents] 
      : Settings;
    const isExpanded = expandedItems.includes(String(itemId));
    const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0;

    // Her seviye için girinti hesapla (level * 16px)
    const indentStyle = level > 0 ? { marginLeft: `${level * 16}px` } : {};

    return (
      <div key={itemId}>
        <div
          className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-xl cursor-pointer transition-all duration-200 group ${
            level === 0
              ? 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
              : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
          style={indentStyle}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(itemId);
            } else if (itemHref) {
              handleNavigation(itemHref);
            }
          }}
        >
          {IconComponent && <IconComponent className="w-5 h-5 mr-3 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />}
          <span className={`flex-1 ${isCollapsed && !isOpen ? 'lg:hidden' : ''}`}>{itemLabel}</span>
          {hasChildren && (
            <div className={`${isCollapsed && !isOpen ? 'lg:hidden' : ''}`}>
              {isExpanded ?
                <ChevronDown className="w-4 h-4" /> :
                <ChevronRight className="w-4 h-4" />
              }
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div
            className="mt-1 space-y-1 border-l-2 border-slate-100 dark:border-slate-800/50"
            style={{ marginLeft: `${(level + 1) * 8}px` }}
          >
            {item.children && Array.isArray(item.children) &&
             item.children
               .filter((child: any) => child)
               .map((child: any) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-r border-slate-200 dark:border-slate-800/80 transform transition-all duration-300 ease-in-out flex flex-col h-full shadow-2xl lg:shadow-none ${
        isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'
      } lg:translate-x-0 lg:static lg:inset-0 ${
        isCollapsed && !isOpen ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-800/80">
          <div
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleDashboardClick}
            title="Ana Sayfaya Git"
          >
            {systemLogo ? (
              <img
                src={systemLogo}
                alt="System Logo"
                className={`rounded-lg object-contain transition-all ${
                  isCollapsed && !isOpen ? 'lg:w-10 lg:h-10' : 'w-14 h-14'
                }`}
              />
            ) : (
              <div className={`bg-primary rounded-lg flex items-center justify-center transition-all ${
                isCollapsed && !isOpen ? 'lg:w-10 lg:h-10' : 'w-14 h-14'
              }`}>
                <Settings className={`text-white ${isCollapsed && !isOpen ? 'lg:w-6 lg:h-6' : 'w-8 h-8'}`} />
              </div>
            )}
            <span className={`ml-3 text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight ${isCollapsed && !isOpen ? 'lg:hidden' : ''}`}>
              {systemTitle}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleCollapse}
              className="hidden lg:block p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            >
              <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? 'rotate-0' : 'rotate-180'}`} />
            </button>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Mobile Search Button - sadece mobilde sidebar açıkken görünür */}
        {isOpen && (
          <div className="lg:hidden px-3 py-2 border-b border-slate-200 dark:border-slate-800/80">
            <button
              onClick={() => {
                onClose();
                setTimeout(() => window.dispatchEvent(new CustomEvent('openMobileSearch')), 300);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <Search className="w-4 h-4" />
              <span>{t('sidebar.search')}</span>
              <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">Ctrl+K</span>
            </button>
          </div>
        )}

        <nav className="mt-5 px-2 space-y-1 flex-1 overflow-y-auto pb-4">
          {menuLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-red-600 text-sm">
              {t('sidebar.menuLoadError')}
            </div>
          ) : !Array.isArray(menuItemsToRender) || menuItemsToRender.length === 0 ? (
            <div className="p-4 text-gray-500 dark:text-gray-400 text-sm">
              {/* ✅ RBAC: Hiç menü yok = User'ın permission'ı yok */}
              {t('sidebar.noMenuAccess')}
            </div>
          ) : (
            // ✅ RBAC: menuItemsToRender (RBAC tarafından filtrelenmiş)
            menuItemsToRender
              .filter(item => item && typeof item === 'object')
              .map(item => renderMenuItem(item))
          )}
        </nav>
        

        {/* Version Info */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800/80">
          <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
            {currentVersion && (
              <div className="flex items-center justify-between">
                <span>{t('sidebar.version')}</span>
                <span className="font-mono">{currentVersion}</span>
              </div>
            )}
            {hasUpdate && (
              <div className="flex items-center text-orange-600 dark:text-orange-400">
                <AlertTriangle className="w-3 h-3 mr-1" />
                <span>{t('header.updateAvailable')}</span>
              </div>
            )}
            {!hasUpdate && currentVersion && (
              <div className="flex items-center text-green-600 dark:text-green-400">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span>{t('header.systemUpToDate')}</span>
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

export default Sidebar;